/**
 * 소싱에서 남기기로 한 옵션(1688 skuId)만 고른다 — AI 로켓등록 processOptions 가 가격을 매기기 전에 쓴다.
 * 순수 함수만 둔다(DOM·store·API 모름).
 */

/** 남길 옵션이 1688 상품에 하나도 없을 때 대기열 항목 실패 사유(사람이 읽는다) */
export const KEEP_NONE_MESSAGE = '남기기로 한 옵션이 이 1688 상품에 하나도 없어 상품을 만들지 않았습니다. 옵션 구성이 바뀌었을 수 있으니 옵션을 다시 확인해 넣어주세요.'

/**
 * keepSkuIds 가 없으면(null·빈 목록) 받은 SKU 를 그대로 돌려준다(사람 화면 경로는 지금 그대로).
 * 번호는 문자열로 맞춰 비교한다(1688 은 숫자로, 대기열은 문자열로 준다).
 *
 * @param {Array} skus - 1688 productSkuInfos
 * @param {Array|null} keepSkuIds - 남길 skuId 목록
 * @returns {{ skus: Array, missing: string[], filtered: boolean }} missing = 1688 상품에 없는 번호
 */
export function pickKeptSkus (skus, keepSkuIds) {
  const list = Array.isArray(skus) ? skus : []
  if (!Array.isArray(keepSkuIds) || keepSkuIds.length === 0) return { skus: list, missing: [], filtered: false }
  const want = new Set(keepSkuIds.map(String))
  const kept = list.filter(sku => sku && want.has(String(sku.skuId)))
  const have = new Set(kept.map(sku => String(sku.skuId)))
  return { skus: kept, missing: [...want].filter(id => !have.has(id)), filtered: true }
}

/**
 * 대기열 항목의 남길 옵션·뺀 옵션을 1688 옵션에 대 «남길 번호» 하나로 푼다(판 #443 ① 뺀 옵션은 그 상품에서 지운다).
 * 남길 = keepSkuIds 가 있으면 그것(1688 에 있는 것만), 없으면 1688 옵션 전부 — 거기서 뺀 옵션을 지운다.
 * 둘 다 없거나 옵션 없는 단일 상품이면 ids = null(거르지 않는다). 다 빠지면 빈 목록 — 부르는 쪽이 실패시킨다.
 *
 * @param {Array} skus - 1688 productSkuInfos
 * @param {Array|null} keepSkuIds - 남길 skuId 목록
 * @param {Array|null} excludedSkuIds - 뺀 skuId 목록
 * @returns {{ ids: string[]|null, missing: string[] }} ids = 1688 순서 · missing = 1688 상품에 없는 번호(남길·뺀 둘 다)
 */
export function resolveKeepSkuIds (skus, keepSkuIds, excludedSkuIds) {
  const list = Array.isArray(skus) ? skus : []
  const out = Array.isArray(excludedSkuIds) ? [...new Set(excludedSkuIds.map(String))] : []
  const picked = pickKeptSkus(list, keepSkuIds)
  const have = new Set(list.map(sku => sku && String(sku.skuId)))
  const missing = [...picked.missing, ...out.filter(id => !have.has(id))]
  if (!out.length) return { ids: picked.filtered ? picked.skus.map(sku => String(sku.skuId)) : null, missing }
  if (!list.length) return { ids: picked.filtered ? [] : null, missing }
  const drop = new Set(out)
  return { ids: picked.skus.map(sku => String(sku.skuId)).filter(id => !drop.has(id)), missing }
}

/** 남길 옵션이 전부 1688 재고 0 일 때 대기열 항목 실패 사유(사람이 읽는다) */
export const STOCK_NONE_MESSAGE = '1688 재고가 남길 옵션 모두 0이라 상품을 만들지 않았습니다. 재고가 있는 공급처로 바꾸거나 판매자에게 재고를 확인한 뒤 다시 넣어주세요.'

const skuName = sku => (Array.isArray(sku.skuAttributes) ? sku.skuAttributes : []).map(a => a && a.value).filter(Boolean).join(' / ')

/** amountOnSale → 수(정수 글자 포함) · 없음·숫자 아님 = null(못 잼) */
function stockOf (sku) {
  const v = sku.amountOnSale
  if (typeof v === 'number') return Number.isFinite(v) && v >= 0 ? v : null
  if (typeof v === 'string' && /^\d+$/.test(v.trim())) return Number(v.trim())
  return null
}

/**
 * 1688 실시간 재고 0 옵션을 뺀다(202E64 — 선택 옵션 재고만 보고 재고 0 옵션까지 올렸다).
 * 재고를 못 잰 옵션(값 없음·숫자 아님)은 0 으로 세지 않고 남긴 채 unknown 에 싣는다.
 * 뺄 것이 없으면 ids 를 그대로 돌려준다(null = 거르지 않음 유지). 다 빠지면 빈 목록 — 부르는 쪽이 실패시킨다.
 *
 * @param {Array} skus - 1688 productSkuInfos
 * @param {string[]|null} ids - resolveKeepSkuIds 의 ids(null = 1688 옵션 전부)
 * @returns {{ ids: string[]|null, soldOut: {skuId:string,name:string}[], unknown: {skuId:string,name:string}[] }}
 */
export function dropSoldOutSkus (skus, ids) {
  const list = (Array.isArray(skus) ? skus : []).filter(Boolean)
  const want = Array.isArray(ids) ? new Set(ids.map(String)) : null
  const scope = want ? list.filter(sku => want.has(String(sku.skuId))) : list
  const soldOut = []
  const unknown = []
  for (const sku of scope) {
    const n = stockOf(sku)
    const entry = { skuId: String(sku.skuId), name: skuName(sku) }
    if (n === null) unknown.push(entry)
    else if (n === 0) soldOut.push(entry)
  }
  if (!soldOut.length) return { ids: Array.isArray(ids) ? ids : null, soldOut, unknown }
  const drop = new Set(soldOut.map(o => o.skuId))
  return { ids: scope.map(sku => String(sku.skuId)).filter(id => !drop.has(id)), soldOut, unknown }
}

// 1688 사진 주소 비교 — 쿼리·프로토콜·알리 썸네일 꼬리(.jpg_.webp · .jpg_400x400.jpg)를 뗀다
const imageKey = url => String(url || '').trim().replace(/^https?:/i, '').replace(/[?#].*$/, '').replace(/(\.(?:jpe?g|png|webp|gif))_.*$/i, '$1').toLowerCase()

/**
 * 뺀 옵션(남길 skuId 밖)의 SKU 사진과 같은 주소인 사진을 뺀다 — 대표·추가·상세 사진이 뺀 옵션 사진으로 만들어졌다(4DBBFE).
 * 남길 옵션의 SKU 사진과도 같은 주소면 남긴다. SKU 사진과 주소가 다른 사진은 어느 옵션 것인지 알 수 없어 그대로 둔다(짐작 0).
 * 다 빠지면 남길 옵션의 SKU 사진으로 채운다(그 옵션 제 사진).
 *
 * @param {string[]} images - 1688 사진 목록(productImage.images · 상세 사진)
 * @param {Array} skus - 1688 productSkuInfos
 * @param {Array|null} keepSkuIds - 남길 skuId 목록(없으면 거르지 않는다)
 * @returns {{ images: string[], removed: string[] }}
 */
export function dropExcludedSkuImages (images, skus, keepSkuIds) {
  const list = Array.isArray(images) ? images : []
  const picked = pickKeptSkus(skus, keepSkuIds)
  if (!picked.filtered || !picked.skus.length) return { images: list, removed: [] }
  const skuImages = sku => (Array.isArray(sku && sku.skuAttributes) ? sku.skuAttributes : []).map(a => a && a.skuImageUrl).filter(Boolean)
  const keptSet = new Set(picked.skus.flatMap(skuImages).map(imageKey))
  const keptIds = new Set(picked.skus.map(sku => String(sku.skuId)))
  const outSet = new Set((Array.isArray(skus) ? skus : []).filter(sku => sku && !keptIds.has(String(sku.skuId))).flatMap(skuImages).map(imageKey).filter(k => !keptSet.has(k)))
  if (!outSet.size) return { images: list, removed: [] }
  const removed = list.filter(u => outSet.has(imageKey(u)))
  if (!removed.length) return { images: list, removed }
  const left = list.filter(u => !outSet.has(imageKey(u)))
  return { images: left.length ? left : [...new Set(picked.skus.flatMap(skuImages))], removed }
}

export default { pickKeptSkus, resolveKeepSkuIds, dropSoldOutSkus, dropExcludedSkuImages, KEEP_NONE_MESSAGE, STOCK_NONE_MESSAGE }
